import { useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { periodLabel, planPrice } from "@/lib/pricing";
import { fmtPlainCVE, CardLogos, PayeeLine, TOKEN_BRANDS } from "@/components/PaymentLogos";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { CircleNotch, LockKey } from "@phosphor-icons/react";
import { toast } from "sonner";

function Option({ id, checked, onChange, disabled, children }) {
  return (
    <label htmlFor={id} className={`flex items-start gap-3 rounded-lg border border-border p-3 text-sm ${disabled ? "opacity-60" : "cursor-pointer"}`}>
      <Checkbox id={id} checked={checked} onCheckedChange={(v) => onChange(!!v)} disabled={disabled} data-testid={id} className="mt-0.5" />
      <span>{children}</span>
    </label>
  );
}

function Line({ label, value, testid, strong }) {
  return (
    <div className={`flex justify-between ${strong ? "mt-1 border-t border-border pt-2 font-display text-2xl font-extrabold" : "text-sm"}`}>
      <span>{label}</span><span data-testid={testid}>{value}</span>
    </div>
  );
}

export function Vinti4CheckoutDialog({ target, config, onClose }) {
  const [accept, setAccept] = useState(false);
  const [saveCard, setSaveCard] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!target) return null;
  const { plan, months, amount } = target;
  const { base, isFounder } = planPrice(plan, months);
  const tokenMode = !!config?.tokenization && saveCard;

  const pay = async () => {
    if (!accept) return toast.error("Tem de aceitar os Termos e a Política de Devolução");
    setBusy(true);
    try {
      const { data } = await api.post("/vinti4/checkout", { plan: plan.key, months, accept_terms: true, save_card: tokenMode });
      window.location.href = data.redirect_url;
    } catch (err) { toast.error(apiError(err)); setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="vinti4-checkout-dialog">
        <DialogHeader>
          <DialogTitle>{tokenMode ? "Pagar com Cartão Vinti4 / Visa" : "Pagar com Cartão Vinti4 / Visa / Mastercard / Amex"}</DialogTitle>
          <DialogDescription>Será redirecionado para a página segura da SISP (Vinti4) para introduzir o cartão.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1 rounded-xl bg-secondary p-4" data-testid="vinti4-summary">
            <Line label={`Plano ${plan.name} · ${periodLabel(months)}`} value={fmtPlainCVE(isFounder ? base : amount)} testid="vinti4-base" />
            {isFounder && base > amount && <Line label={`Desconto (−${Math.round((1 - amount / base) * 100)}%)`} value={`−${fmtPlainCVE(base - amount)}`} testid="vinti4-discount" />}
            <Line label="Total" value={fmtPlainCVE(amount)} testid="vinti4-amount" strong />
            <div className="text-xs text-muted-foreground">Sem taxas extra por pagar com cartão. Recebe um Recibo por email com a referência de pagamento.</div>
          </div>
          <CardLogos className="h-5" brands={tokenMode ? TOKEN_BRANDS : undefined} />
          <PayeeLine className="text-muted-foreground" />
          <Option id="vinti4-accept-terms" checked={accept} onChange={setAccept}>
            Aceito os <Link to="/termos" target="_blank" className="font-semibold text-primary underline">Termos</Link> e a{" "}
            <Link to="/politica-de-devolucao" target="_blank" className="font-semibold text-primary underline">Política de Devolução</Link>
          </Option>
          <Option id="vinti4-save-card" checked={tokenMode} onChange={setSaveCard} disabled={!config?.tokenization}>
            Guardar o cartão para renovações automáticas (só Cartão Vinti4 / Visa) {!config?.tokenization && <span className="text-xs text-muted-foreground">(brevemente — aguarda aprovação SISP)</span>}
          </Option>
          <Option id="vinti4-installments" checked={false} onChange={() => {}} disabled={!config?.installments}>
            Pagar em prestações <span className="text-xs text-muted-foreground">(brevemente — aguarda aprovação SISP)</span>
          </Option>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={pay} disabled={busy || !accept} data-testid="vinti4-pay-btn">
            {busy ? <CircleNotch className="animate-spin" /> : <><LockKey size={16} className="mr-1.5" /> Pagar {fmtPlainCVE(amount)}</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
